import React, {useCallback, useEffect, useMemo, useState} from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { FiTrash2 } from "react-icons/fi";
import EventButton from "./EventButton";
import { filterEvents } from "../../utils/filters.js";

const savedEventKey = (event, index) => event?.assessmentId
  ? `assessment:${event.assessmentId}`
  : `${event?.cur || "event"}:${event?.ts || event?.name || index}`;

function Section(props) {
  const [collapsed, setCollapsed] = useState(false);
  const [showClearSavedConfirmation, setShowClearSavedConfirmation] = useState(false);
  const savedFilters = useMemo(() => ({ ...props.filters, aiValidatedOnly: false }), [props.filters]);

  const filteredEvents = useMemo(() => filterEvents(
      props.section?.events || [],
      props.filters,
      props.insights?.currencyInfo,
      props.currencies
  ), [props.section?.events, props.filters, props.insights?.currencyInfo, props.currencies, props.reloadAttrs]);
  const filteredFavourites = useMemo(() => filterEvents(
      props.section?.favourites || [],
      savedFilters,
      props.insights?.currencyInfo,
      props.currencies
  ), [props.section?.favourites, savedFilters, props.insights?.currencyInfo, props.currencies, props.reloadAttrs]);
  const filteredStarred = useMemo(() => filterEvents(
      props.section?.starred || [],
      savedFilters,
      props.insights?.currencyInfo,
      props.currencies
  ), [props.section?.starred, savedFilters, props.insights?.currencyInfo, props.currencies, props.reloadAttrs]);
  const displayedEvents = props.section?.favourites
      ? filteredFavourites
      : props.section?.starred
          ? filteredStarred
          : filteredEvents;

    useEffect(() => {
        const isCollapsed = props.settings?.sectionSettings?.[props.section.title]?.isCollapsed || false;
        setCollapsed(isCollapsed);
    }, [props.settings, props.reloadAttrs]);

  const openPopup = props.openPopup;
  const openPopupFullInfo = useCallback((data) => {

      const events = displayedEvents;

      let fullInfo = {
          index: events.indexOf(data),
          sectionEvents: events
      }

      openPopup(fullInfo);
  }, [displayedEvents, openPopup]);

  if (displayedEvents.length === 0) {
      return null;
  }

  const toggleCollapse = () => {
      const settings = { ...(props.settings || {}) };
      const sectionSettings = { ...(settings.sectionSettings || {}) };
      const title = props.section.title;
      sectionSettings[title] = { ...(sectionSettings[title] || {}), isCollapsed: !collapsed };
      settings.sectionSettings = sectionSettings;
      setCollapsed(!collapsed);
      props.setSettings(settings);
  };

  const isFavourites = props.section?.title === "FAVOURITES";
  const isStarred = props.section?.title === "STARRED";
  const savedType = isStarred ? "starred" : isFavourites ? "favourites" : null;
  const savedCount = isStarred
      ? props.section?.starred?.length || 0
      : props.section?.favourites?.length || 0;
  const savedLabel = isStarred ? "starred" : "favourite";

  const openClearSavedConfirmation = (event) => {
      event.stopPropagation();
      setShowClearSavedConfirmation(true);
  };

  const closeClearSavedConfirmation = (event) => {
      event?.stopPropagation();
      setShowClearSavedConfirmation(false);
  };

  const clearSaved = (event) => {
      event.stopPropagation();
      props.setSettings({
          ...props.settings,
          [savedType]: {}
      });
      setShowClearSavedConfirmation(false);
  };

  return (
    <div
      id="sectionGroup"
      className={
        isStarred ? "sectionGroup sectionGroup--starred" :
        isFavourites ? "sectionGroup sectionGroup--favourites" : "sectionGroup"
      }
      onClick={toggleCollapse}
    >
      <h3 
        className="sectionHeader"
        title={props.section.desc}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <span style={{ 
            fontSize: '12px',
            transition: 'transform 0.2s ease',
            transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)'
        }}>
          ▼
        </span>
        {props.section.title} ({displayedEvents.length})
        {savedType && savedCount > 0 && (
          <button
            type="button"
            className="clearSavedButton"
            onClick={openClearSavedConfirmation}
            title={`Delete all ${savedLabel} items`}
            aria-label={`Delete all ${savedLabel} items`}
          >
            <FiTrash2 aria-hidden="true" />
          </button>
        )}
      </h3>
      {!collapsed && (
        <section id="sectionData">
          {filteredEvents ? filteredEvents.map((event) => (
            <EventButton
              key={event.cur + event.ts}
              event={event}
              openPopup={openPopupFullInfo}
            />
          )) : null}

        {props.section.favourites ? filteredFavourites.map((fav) => (
            <div key={fav.cur}>
                <EventButton
                    event={fav}
                    openPopup={openPopupFullInfo}/>
            </div>
        )) : null}
        {props.section.starred ? filteredStarred.map((star, index) => (
            <div key={savedEventKey(star, index)}>
                <EventButton
                    event={star}
                    openPopup={openPopupFullInfo}/>
            </div>
        )) : null}
        </section>
      )}
      <Modal
        show={showClearSavedConfirmation}
        onHide={closeClearSavedConfirmation}
        onClick={(event) => event.stopPropagation()}
        centered
        contentClassName="clearSavedModal"
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>Delete {savedLabel} items?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will permanently remove all {savedCount} {savedLabel} {savedCount === 1 ? "item" : "items"}.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeClearSavedConfirmation}>
            Cancel
          </Button>
          <Button variant="danger" onClick={clearSaved}>
            Delete all
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Section;
